"use client"

import { useTranslations } from "next-intl"
import { useCharacterStore } from "@/features/characters/store"
import { Label } from "@/shared/components/ui/label"
import { Slider } from "@/shared/components/ui/slider"
import { cn } from "@/shared/lib/utils"
import { Icon } from "@/shared/components/icons"
import { localizeCreateOptions } from "@/features/create/lib/create-option-labels"
import { ImageCardGrid, ColorSwatchPicker, ETHNICITY_OPTIONS } from "./appearance-step"

const AGE_MIN = 18
const AGE_MAX = 99

// One rolling digit column: a 0–9 strip translated to show the current digit via
// a plain CSS transform transition — GPU-smooth, no mount/unmount, and only the
// digits that actually change move (21→22 rolls just the units column). The
// slightly-overshooting easing gives it a lively spring-like flip.
function AgeDigit({ digit }: { digit: number }) {
  return (
    <span className="relative inline-block h-[1em] w-[1ch] overflow-hidden">
      <span
        className="absolute inset-x-0 top-0 flex flex-col transition-transform duration-[450ms] ease-[cubic-bezier(0.34,1.4,0.5,1)]"
        style={{ transform: `translateY(-${digit * 10}%)` }}
      >
        {Array.from({ length: 10 }, (_, n) => (
          <span key={n} className="flex h-[1em] items-center justify-center">
            {n}
          </span>
        ))}
      </span>
    </span>
  )
}

// Age readout as an odometer — one rolling column per digit.
function RollingAge({ age }: { age: number }) {
  const digits = String(age).split("").map(Number)
  return (
    <div className="flex items-center justify-center leading-none text-5xl font-bold tabular-nums text-foreground sm:text-6xl">
      {digits.map((d, i) => (
        <AgeDigit key={i} digit={d} />
      ))}
    </div>
  )
}

/**
 * Skin tone palette. `id` is what we store in `draft.skinTone` — a string in
 * the 0..10 numeric range that the edge function interpolates into both the
 * skin slider strength AND `skinColor_clip`. Leaving `skinTone === ""` (no
 * tile selected) cancels the skin-color LoRA entirely.
 */
const SKIN_TONE_OPTIONS = [
  { id: "2", labelKey: "fair", hex: "#f5dcb8" },
  { id: "4", labelKey: "light", hex: "#eec39a" },
  { id: "6", labelKey: "medium", hex: "#c99873" },
  { id: "7", labelKey: "tan", hex: "#a06a41" },
  { id: "9", labelKey: "brown", hex: "#6b4029" },
  { id: "10", labelKey: "dark", hex: "#3a2216" },
] as const

export function EthnicityStep({ invalidFields = [] }: { invalidFields?: string[] }) {
  const t = useTranslations("create.wizard.ethnicityStep")
  const tOptions = useTranslations("create.wizard.optionLabels")
  const { draft, updateDraft } = useCharacterStore()
  const currentGender = draft.gender || "female"
  const currentStyle = draft.style || "realistic"
  const age = draft.age ?? 25
  const selectedSkinToneId = draft.skinTone ?? ""
  const isEthnicityInvalid = invalidFields.includes("ethnicity")
  const localizedEthnicityOptions = localizeCreateOptions(ETHNICITY_OPTIONS, "ethnicity", tOptions)
  const localizedSkinToneOptions = SKIN_TONE_OPTIONS.map((option) => ({
    name: tOptions(`skinTone.${option.labelKey}`),
    color: option.hex,
    value: option.id,
  }))

  return (
    <div className="space-y-6">
      <div
        className={cn(
          "space-y-3 rounded-[6px] px-1.5 py-4 transition-all",
          isEthnicityInvalid && "ring-1 ring-red-500/40 border border-red-500/30 bg-red-500/[0.03]"
        )}
      >
        <Label className={cn(
          "text-lg font-bold block text-center transition-colors",
          isEthnicityInvalid ? "text-red-400" : "text-foreground"
        )}>
          {t("title")}
        </Label>
        <p className="text-sm text-muted-foreground text-center">
          {t("description")}
        </p>
        <ImageCardGrid
          options={localizedEthnicityOptions}
          selected={draft.ethnicity}
          onSelect={(v) => updateDraft({ ethnicity: v })}
          gender={currentGender}
          style={currentStyle}
          columns={5}
        />
        {isEthnicityInvalid && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-3.5 py-2.5 mt-2">
            <Icon name="attention-circle" size={16} className="size-4 shrink-0 text-red-400" />
            <p className="text-sm text-red-400">{t("requiredError")}</p>
          </div>
        )}
      </div>

      {/* Skin tone — swatch row. Tapping a selected tile clears it (LoRA neutral). */}
      <div className="space-y-2">
        <Label className="text-lg font-bold block text-center text-foreground">
          {t("skinToneTitle")}
        </Label>
        <p className="text-sm text-muted-foreground text-center">
          {t("skinToneDescription")}
        </p>
        <ColorSwatchPicker
          options={localizedSkinToneOptions}
          selected={selectedSkinToneId}
          onSelect={(v) => updateDraft({ skinTone: v === selectedSkinToneId ? "" : v })}
        />
      </div>

      {/* Age — premium block */}
      <div className="rounded-[6px] border border-border/40 bg-muted/10 p-4 sm:p-5">
        <div className="flex flex-col items-center gap-4">
          <div className="text-center">
            <p className="text-sm font-medium text-muted-foreground mb-0.5">
              {t("ageTitle")}
            </p>
            <RollingAge age={age} />
            <p className="text-sm font-medium text-muted-foreground mt-0.5">
              {t("yearsOld")}
            </p>
          </div>

          {/* Slider — brand fill, comfortable touch area */}
          <div className="w-full max-w-xs [&_[data-slot=slider-range]]:bg-primary [&_[data-slot=slider-track]]:bg-border/40 [&_[data-slot=slider-thumb]]:size-5 [&_[data-slot=slider-thumb]]:border-primary [&_[data-slot=slider-thumb]]:bg-white">
            <Slider
              min={AGE_MIN}
              max={AGE_MAX}
              value={[age]}
              onValueChange={(v) => updateDraft({ age: v[0] ?? AGE_MIN })}
              className="w-full py-4"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("ageHelp")}
          </p>
        </div>
      </div>
    </div>
  )
}
